import {
  ApolloClient,
  HttpLink,
  InMemoryCache,
  from,
  Observable,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { persistentStorage } from '@/lib/storage';
import { env } from '@/lib/env';
import { logger } from '@/lib/logger';
import { authVar } from './auth';
import result from '~/graphql/generated/gql-possible-types';
import { refreshToken } from '@/lib/auth/refresh-token';

const createAuthLink = () => {
  return setContext(async (_, { headers }) => {
    const tokens = await persistentStorage.get('auth');
    return {
      headers: {
        ...headers,
        ...(tokens?.accessToken && {
          Authorization: `Bearer ${tokens.accessToken}`,
        }),
      },
    };
  });
};

const createAuthRetryErrorLink = () => {
  let isRefreshing = false;
  let pendingRequests: Array<() => void> = [];

  const resolvePendingRequests = () => {
    pendingRequests.forEach((callback) => callback());
    pendingRequests = [];
  };

  return onError(({ graphQLErrors, networkError, operation, forward }) => {
    logger.debug('onError called with:', {
      graphQLErrors: graphQLErrors?.map((err) => ({
        message: err.message,
        code: err.extensions?.code,
        originalError: err.extensions?.originalError,
      })),
      networkError: networkError?.message,
    });

    // Check if it's an authentication error
    const isAuthError = graphQLErrors?.some(
      (err) =>
        err.extensions?.code === 'UNAUTHENTICATED' ||
        err.message === 'User validation failed' ||
        (err.extensions?.originalError as { statusCode?: number })
          ?.statusCode === 401,
    );

    if (!isAuthError) {
      return;
    }

    logger.debug('Auth error detected, attempting token refresh');

    const currentAuth = authVar();
    if (!currentAuth?.refreshToken) {
      logger.error('No refresh token available, clearing auth');
      authVar(null);
      void persistentStorage.set('auth', null);
      if (typeof window !== 'undefined') {
        window.location.href = '/';
      }
      return;
    }

    // If already refreshing, queue this request
    if (isRefreshing) {
      logger.debug('Token refresh in progress, queuing request');
      return new Observable((observer) => {
        pendingRequests.push(() => {
          forward(operation).subscribe(observer);
        });
      });
    }

    isRefreshing = true;

    // Attempt to refresh the token
    return refreshToken(currentAuth.refreshToken)
      .then(() => {
        logger.debug('Token refreshed, retrying failed requests');
        isRefreshing = false;
        resolvePendingRequests();
        return forward(operation);
      })
      .catch((error) => {
        logger.error('Token refresh failed:', error);
        isRefreshing = false;
        pendingRequests = [];

        // Clear auth and redirect to login
        authVar(null);
        void persistentStorage.set('auth', null);
        if (typeof window !== 'undefined') {
          window.location.href = '/';
        }
      });
  });
};

const createApolloLink = ({ httpUri }: { httpUri: URL }) => {
  const authRetryErrorLink = createAuthRetryErrorLink();
  const authLink = createAuthLink();

  // Create simple HTTP link (no batching) for the federation gateway
  const httpLink = new HttpLink({
    uri: httpUri.toString(),
    headers: {},
  });

  // Combine HTTP links: error handling -> auth -> http
  return from([authRetryErrorLink, authLink, httpLink]);
};

const createApolloCache = () =>
  new InMemoryCache({
    possibleTypes: result.possibleTypes,
    typePolicies: {
      Query: {
        fields: {},
      },
    },
  });

export const createApolloClient = ({ httpUri }: { httpUri: URL }) => {
  const apolloLink = createApolloLink({ httpUri });
  return new ApolloClient({
    cache: createApolloCache(),
    link: apolloLink,
    devtools: {
      enabled: env.nodeEnv === 'development',
    },
  });
};
